import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
	Button,
	Card,
	CardBody,
	CardHeader,
	CardFooter,
	UncontrolledDropdown,
	DropdownToggle,
	DropdownMenu,
	DropdownItem,
} from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import 'moment/locale/es';

import { getHourMeterSummary } from '../../../../../slices/thunks';
import EChartsBarChart from '../../../../../Components/Common/Charts/ECharts/EChartsBarChart';
import { ECHARTS_COLOR_PALETTE } from '../../../../../Components/Hooks/useColorArrayHooks';

moment.locale('es');

// ─── Helpers ────────────────────────────────────────────────────────────

const formatMap = {
	'2A': 'YYYY',
	'1A': 'YYYY',
	'6M': 'MM-YYYY',
	'3M': 'MM-YYYY',
	'1M': 'YYYY-[S]WW',
	AC: 'MM-YYYY',
};

const formatDate = (date, filter) => {
	return moment(date).format(formatMap[filter] || 'DD-MM-YYYY');
};

const prepareChartDataPowerECharts = (data, filter) => {
	const grouped = data.reduce((acc, item) => {
		if (!item.register_date || !item.power_generation_source_info) return acc;
		const date = formatDate(item.register_date, filter);
		const generator =
			item.power_generation_source_info.name || 'Generador Desconocido';
		const barge = item.feeding_barges_info?.name?.trim() || '';
		const key = barge ? `${barge} - ${generator}` : generator;
		const compositeKey = `${date}-${key}`;
		acc[compositeKey] = acc[compositeKey] || { date, key, hours: 0 };
		acc[compositeKey].hours += Number(item.hours) || 0;
		return acc;
	}, {});

	const sorted = Object.values(grouped).sort((a, b) =>
		moment(a.date).diff(moment(b.date))
	);
	const categories = [...new Set(sorted.map(d => d.date))];
	const generators = [...new Set(sorted.map(d => d.key))];

	const series = generators.map(name => ({
		name,
		type: 'bar',
		barGap: 0,
		emphasis: {
			focus: 'series',
		},
		data: categories.map(date => {
			const match = sorted.find(d => d.date === date && d.key === name);
			return match?.hours || 0;
		}),
	}));

	return {
		series,
		categories,
		legendData: generators,
	};
};

const prepareChartDataBalanceECharts = (data, filter) => {
	const dateFormat = formatMap[filter] || 'DD-MM-YYYY';

	const grouped = data.reduce((acc, item) => {
		if (!item.register_date) return acc;
		const date = formatDate(item.register_date, filter);
		acc[date] = acc[date] || { date, hours: 0 };
		acc[date].hours += Number(item.hours) || 0;
		return acc;
	}, {});

	const sorted = Object.values(grouped).sort((a, b) =>
		moment(a.date, dateFormat).diff(moment(b.date, dateFormat))
	);

	const categories = sorted.map(d => d.date);

	const series = [
		{
			name: 'Total Horas',
			type: 'bar',
			barGap: 0,
			emphasis: {
				focus: 'series',
			},
			data: categories.map(date => {
				const d = sorted.find(i => i.date === date);
				return d?.hours || 0;
			}),
		},
	];

	return {
		series,
		categories,
		legendData: ['Total Horas'],
	};
};

// ─── Componente Principal ────────────────────────────────────────────────

const HourMeterPowerGenerationEChart = ({
	establishmentId,
	title,
	legendPosition = 'right',
	option,
	um = 'Horas',
	minHeight,
	maxHeight,
}) => {
	const dispatch = useDispatch();
	const [selectedFilter, setSelectedFilter] = useState(option);
	const [selectedGroup, setSelectedGroup] = useState('hour_power');

	const infoPower = useSelector(
		state => state.FuelSupply.hourmeterSummary?.hour_power || null
	);
	const infoHour = useSelector(
		state => state.FuelSupply.hourmeterSummary?.hour_balance || null
	);

	const fetchConsumptionData = useCallback(
		(idEstablishment, filter) => {
			if (selectedGroup === 'hour_power') {
				dispatch(
					getHourMeterSummary({
						establishment_id: idEstablishment,
						filter: filter,
						type: 'hour_power',
					})
				);
			} else {
				dispatch(
					getHourMeterSummary({
						establishment_id: idEstablishment,
						filter: filter,
						type: 'hour_balance',
					})
				);
			}
		},
		[dispatch, selectedGroup]
	);

	useEffect(() => {
		if (!establishmentId) return;
		fetchConsumptionData(establishmentId, selectedFilter);
	}, [establishmentId, selectedFilter, fetchConsumptionData]);

	const { series, categories, legendData } = useMemo(() => {
		if (selectedGroup === 'hour_power' && Array.isArray(infoPower?.data)) {
			return prepareChartDataPowerECharts(infoPower.data, selectedFilter);
		} else if (
			selectedGroup === 'hour_balance' &&
			Array.isArray(infoHour?.data)
		) {
			return prepareChartDataBalanceECharts(infoHour.data, selectedFilter);
		}
		return { series: [], categories: [], legendData: [] };
	}, [infoPower?.data, infoHour?.data]);

	const options = useMemo(
		() => ({
			color: ECHARTS_COLOR_PALETTE,
			tooltip: {
				trigger: 'axis',
				axisPointer: {
					type: 'shadow',
				},
			},
			legend: {
				data: legendData,
				orient: 'horizontal',
				bottom: 10,
				type: 'scroll',
			},
			grid: {
				top: '12%',
				left: '3%',
				right: '4%',
				bottom: '15%',
				containLabel: true,
			},
			xAxis: [
				{
					type: 'category',
					data: categories,
				},
			],
			yAxis: [
				{
					type: 'value',
					name: um,
				},
			],
			series: series,
		}),
		[series, categories, legendData, um]
	);

	const noData =
		selectedGroup === 'hour_power'
			? !infoPower?.data?.length
			: !infoHour?.data?.length;

	return (
		<Card className='m-0 h-100'>
			<CardHeader className='p-2 align-items-center d-flex'>
				<h5 className='card-title mb-0 flex-grow-1'>{title}</h5>
				<UncontrolledDropdown className='card-header-dropdown me-2'>
					<DropdownToggle
						tag='a'
						className='text-reset dropdown-btn'
						role='button'
					>
						<span className='fs-11'>Agrupado por: </span>
						<span className='text-muted fw-bold text-uppercase'>
							{selectedGroup === 'hour_power'
								? 'Fuente de Generación de Energía'
								: 'Consumo General'}
							<i className='mdi mdi-chevron-down ms-1'></i>
						</span>
					</DropdownToggle>
					<DropdownMenu className='dropdown-menu-end'>
						<DropdownItem
							onClick={() => setSelectedGroup('hour_balance')}
							active={selectedGroup === 'hour_balance'}
						>
							Consumo General
						</DropdownItem>
						<DropdownItem
							onClick={() => setSelectedGroup('hour_power')}
							active={selectedGroup === 'hour_power'}
						>
							Fuente de Generación de Energía
						</DropdownItem>
					</DropdownMenu>
				</UncontrolledDropdown>
				<div className='btn-box-dashboard'>
					{['2A', '1A', '6M', '3M', '1M'].map(f => (
						<Button
							key={f}
							color={selectedFilter === f ? 'primary' : 'secondary'}
							size='sm'
							className={`btn-soft-${
								selectedFilter === f ? 'primary' : 'secondary'
							} me-1`}
							onClick={() => setSelectedFilter(f)}
						>
							{f}
						</Button>
					))}
				</div>
			</CardHeader>

			{noData ? (
				<>
					<CardBody className='p-2'>
						<div className='d-flex justify-content-center align-items-center p-3'>
							<div className='text-center'>
								<div className='avatar-md'>
									<span className='avatar-title bg-info-subtle rounded-circle fs-1'>
										<i className='ri-gas-station-fill text-info'></i>
									</span>
								</div>
							</div>
						</div>
					</CardBody>
					<CardFooter className='m-0 p-2 text-center'>
						<h6 className='mb-0 p-0 text-wrap w-100'>
							<span className='fs-11 badge bg-info-subtle text-info text-wrap'>
								No cuenta con registro de consumo de combustible
							</span>
						</h6>
					</CardFooter>
				</>
			) : (
				<CardBody className='p-0'>
					<EChartsBarChart
						options={options}
						minHeight={minHeight}
						maxHeight={maxHeight}
					/>
				</CardBody>
			)}
		</Card>
	);
};

export default HourMeterPowerGenerationEChart;
